import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ats_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors.
// Previously ANY 401 from ANY endpoint instantly wiped the session and
// hard-redirected to /login, which threw users out mid-work whenever a
// single background call failed (the long-standing "random logout" bug).
// Now we only force a logout after confirming against /auth/me that the
// token is really dead, and we remember where the user was so login can
// bring them straight back.
let sessionCheck = null;

const isTokenReallyDead = () => {
  if (!sessionCheck) {
    sessionCheck = axios
      .get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('ats_token')}` },
      })
      .then(() => false)
      .catch((err) => err?.response?.status === 401)
      .finally(() => {
        setTimeout(() => {
          sessionCheck = null;
        }, 5000);
      });
  }
  return sessionCheck;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      const isAuthEndpoint =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/request-otp') ||
        requestUrl.includes('/auth/verify-otp') ||
        requestUrl.includes('/auth/me');
      const onLoginPage = window.location.pathname.startsWith('/login');
      const token = localStorage.getItem('ats_token');

      if (!isAuthEndpoint && !onLoginPage && token) {
        const dead = await isTokenReallyDead();
        if (dead) {
          try {
            sessionStorage.setItem(
              'ats_return_to',
              window.location.pathname + window.location.search
            );
          } catch (e) {
            // storage unavailable - continue with plain logout
          }
          localStorage.removeItem('ats_token');
          localStorage.removeItem('ats_user');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  requestOtp: (phone) => api.post('/auth/request-otp', { phone }),
  verifyOtp: (phone, otp) => api.post('/auth/verify-otp', { phone, otp }),
  impersonate: (userId) => api.post(`/auth/impersonate/${userId}`),
  sendPasswordReset: (userId, method) => api.post(`/auth/send-password-reset/${userId}`, { method }),
  sendCandidatePasswordReset: (candidateId, method) => api.post(`/auth/send-candidate-password-reset/${candidateId}`, { method }),
};

// Users
export const usersApi = {
  list: () => api.get('/users'),
  getAll: () => api.get('/users'),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Candidates
export const candidatesApi = {
  list: (params) => api.get('/candidates', { params }),
  get: (id) => api.get(`/candidates/${id}`),
  create: (data) => api.post('/candidates', data),
  update: (id, data) => api.put(`/candidates/${id}`, data),
  delete: (id) => api.delete(`/candidates/${id}`),
  bulkDelete: (ids) => api.post('/candidates/bulk-delete', { ids }),
  verify: (id, data) => api.post(`/candidates/${id}/verify`, data),
  bulkVerify: (ids) => api.post('/candidates/bulk-verify', { ids }),
  powerMatch: (id, jobId, providers) => api.post(`/candidates/${id}/power-match`, { job_id: jobId, providers }),
  powerParse: (id, providers) => api.post(`/candidates/${id}/power-parse`, { providers }),
  applyPowerParse: (id, provider, parsedData, customFields = {}, saveDefinitions = true) =>
    api.post(`/candidates/${id}/apply-power-parse`, {
      provider,
      parsed_data: parsedData,
      custom_fields: customFields,
      save_custom_field_definitions: saveDefinitions
    }),
  generateAISummary: (id) => api.post(`/candidates/${id}/generate-ai-summary`),
  uploadCV: (file, aiProvider) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ai_provider', aiProvider);
    return api.post('/candidates/upload-cv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkUploadBatch: (files, aiProvider, sendWhatsapp = true) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('ai_provider', aiProvider);
    formData.append('send_whatsapp', sendWhatsapp);
    return api.post('/candidates/bulk-upload-batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  createBulkBatch: (aiProvider, sendWhatsapp, totalFiles) => {
    const formData = new FormData();
    formData.append('ai_provider', aiProvider);
    formData.append('send_whatsapp', sendWhatsapp);
    formData.append('total_files', totalFiles);
    return api.post('/candidates/bulk-upload-batch/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  appendToBulkBatch: (batchId, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post(`/candidates/bulk-upload-batch/${batchId}/append`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  startBulkBatch: (batchId) => api.post(`/candidates/bulk-upload-batch/${batchId}/start`),
  getBulkUploadStatus: (batchId) => api.get(`/candidates/bulk-upload-batch/${batchId}/status`),
  listBulkUploadBatches: (limit = 10) => api.get('/candidates/bulk-upload-batches', { params: { limit } }),
  // Bulk Power Parse (like bulk upload batch technique)
  bulkPowerParse: (candidateIds, aiProvider = 'anthropic') =>
    api.post('/candidates/bulk-power-parse', { candidate_ids: candidateIds, ai_provider: aiProvider }),
  getBulkPowerParseStatus: () => api.get('/candidates/bulk-power-parse/status'),
  stopBulkPowerParse: () => api.post('/candidates/bulk-power-parse/stop'),
  addNote: (id, note) => api.post(`/candidates/${id}/notes`, { note }),
  deleteNote: (id, noteId) => api.delete(`/candidates/${id}/notes/${noteId}`),
  getShareHistory: (id) => api.get(`/candidates/${id}/share-history`),
};

// Companies
export const companiesApi = {
  list: (params) => api.get('/companies', { params }),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getJobs: (id) => api.get(`/companies/${id}/jobs`),
  assignJobs: (id, jobIds) => api.post(`/companies/${id}/assign-jobs`, { job_ids: jobIds }),
  getAllHrContacts: () => api.get('/companies/hr-contacts/all'),
};

// Custom Fields
export const customFieldsApi = {
  getDefinitions: (entityType = 'candidate') => api.get('/custom-field-definitions', { params: { entity_type: entityType } }),
};

// Jobs
export const jobsApi = {
  list: (params) => api.get('/jobs', { params }),
  get: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  bulkDelete: (ids) => api.post('/jobs/bulk-delete', { ids }),
  bulkUpdateBilling: (jobIds, billingData) => api.post('/jobs/bulk-update-billing', { job_ids: jobIds, ...billingData }),
  bulkUpdateBillingIndividual: (updates) => api.post('/jobs/bulk-update-billing-individual', { updates }),
  bulkAssignCompany: (jobIds, companyId) => api.post('/jobs/bulk-assign-company', { job_ids: jobIds, company_id: companyId }),
  getDealCloser: (id, params) => api.get(`/jobs/${id}/deal-closer`, { params }),
  uploadJD: (file, aiProvider) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ai_provider', aiProvider);
    return api.post('/jobs/upload-jd', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Money Alerts
export const alertsApi = {
  getMoneyAlerts: () => api.get('/money-alerts'),
};

// AI Insights
export const insightsApi = {
  getInsights: () => api.get('/insights'),
  refreshInsights: (forceAi = false) => api.post(`/insights/refresh?force_ai=${forceAi}`),
};

// Matches
export const matchesApi = {
  list: (params) => api.get('/matches', { params }),
  get: (id) => api.get(`/matches/${id}`),
  recalculate: () => api.post('/matches/recalculate'),
  getFilterOptions: () => api.get('/matches/filter-options'),
  getFilterPresets: () => api.get('/matches/filter-presets'),
  saveFilterPreset: (name, filters) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('filters', JSON.stringify(filters));
    return api.post('/matches/filter-presets', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteFilterPreset: (presetId) => api.delete(`/matches/filter-presets/${presetId}`),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  reset: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/settings/reset', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getNotificationStatus: () => api.get('/settings/notification-status'),
  getWhatsAppTemplates: () => api.get('/settings/whatsapp-templates'),
  testEmail: (toEmail) => {
    const formData = new FormData();
    formData.append('to_email', toEmail);
    return api.post('/settings/test-email', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  testWhatsApp: (phone, templateName) => {
    const formData = new FormData();
    formData.append('phone', phone);
    if (templateName) formData.append('template_name', templateName);
    return api.post('/settings/test-whatsapp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReminderEffectiveness: () => api.get('/settings/reminder-effectiveness'),
};

// Role Permissions
export const rolePermissionsApi = {
  getAll: () => api.get('/role-permissions'),
  update: (role, permissions) => api.put(`/role-permissions/${role}`, permissions),
};

// Access Logs
export const accessLogsApi = {
  list: (params) => api.get('/access-logs', { params }),
  create: (data) => api.post('/access-logs', data),
  addNotes: (logId, notes, callStatus) => api.post(`/access-logs/${logId}/notes`, { notes, call_status: callStatus }),
};

// Candidate Timeline
export const timelineApi = {
  get: (candidateId) => api.get(`/candidates/${candidateId}/timeline`),
  add: (candidateId, data) => api.post(`/candidates/${candidateId}/timeline`, data),
};

// Reveal Sensitive Data
export const revealApi = {
  reveal: (candidateId, field) => api.post(`/candidates/${candidateId}/reveal/${field}`),
};

// Activity Logs
export const activityApi = {
  list: (params) => api.get('/activity-logs', { params }),
  log: (action, entityType, entityId = null, entityName = null, details = null, metadata = {}) =>
    api.post('/activity-logs', { action, entity_type: entityType, entity_id: entityId, entity_name: entityName, details, metadata }),
  logPageVisit: (pageName, pageUrl) =>
    api.post('/activity-logs', { action: 'page_visit', entity_type: 'page', entity_name: pageName, metadata: { url: pageUrl } }).catch(() => {}),
};

// Notifications
export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Dashboard
export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
};

// Power Matches
export const powerMatchesApi = {
  list: (params) => api.get('/power-matches', { params }),
  save: (data) => api.post('/power-matches/save', data),
  getForCandidate: (candidateId) => api.get(`/candidates/${candidateId}/power-matches`),
};

// Email Templates
export const emailTemplatesApi = {
  list: () => api.get('/email-templates'),
  get: (id) => api.get(`/email-templates/${id}`),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    return api.post('/email-templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  generate: (prompt, llmProvider) => api.post('/email-templates/generate', { prompt, llm_provider: llmProvider }),
};

// Emails
export const emailsApi = {
  send: (candidateIds, subject, body, templateId = null) =>
    api.post('/emails/send', { candidate_ids: candidateIds, subject, body, template_id: templateId }),
};

// Email Tracking
export const emailTrackingApi = {
  list: (params) => api.get('/email-tracking', { params }),
  getForCandidate: (candidateId) => api.get('/email-tracking', { params: { candidate_id: candidateId } }),
};

// HR Pipeline
export const hrPipelineApi = {
  share: (data) => {
    const formData = new FormData();
    formData.append('match_id', data.match_id);
    formData.append('hr_email', data.hr_email);
    if (data.hr_name) formData.append('hr_name', data.hr_name);
    if (data.hr_phone) formData.append('hr_phone', data.hr_phone);
    return api.post('/hr-pipeline/share', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkShare: (data) => api.post('/hr-pipeline/bulk-share-matches', data),
  getStats: () => api.get('/hr-pipeline/stats'),
  getTimeAnalytics: (period = 'daily', days = 30) => api.get('/hr-pipeline/time-analytics', { params: { period, days } }),
  getCandidateTimeline: (params) => api.get('/hr-pipeline/candidate-timeline', { params }),
  getReviewerPerformance: () => api.get('/hr-pipeline/reviewer-performance'),
  listShares: (params) => api.get('/hr-pipeline/shares', { params }),
  getSettings: () => api.get('/hr-pipeline/settings'),
  updateSettings: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, data[key]);
      }
    });
    return api.put('/hr-pipeline/settings', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Admin status change
  adminChangeStatus: (shareId, newStatus, reason) => {
    const formData = new FormData();
    formData.append('new_status', newStatus);
    if (reason) formData.append('reason', reason);
    return api.post(`/hr-pipeline/admin-status-change/${shareId}`, formData);
  },
  // View stats
  getViewStats: (shareId) => api.get(`/hr-pipeline/view-stats/${shareId}`),
  // Track admin view
  trackView: (token, viewerType = 'admin') => api.post(`/hr-pipeline/track-view/${token}?viewer_type=${viewerType}`),
  // Hired candidates
  markHired: (shareId, data) => api.post(`/hr-pipeline/mark-hired/${shareId}`, data),
  getHiredCandidates: (params) => api.get('/hr-pipeline/hired-candidates', { params }),
  getHiredStats: () => api.get('/hr-pipeline/hired-stats'),
};

export default api;
